/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties} from "react";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {tableCardStyle} from "../../home/AlertsTab";
import {TS_Table} from "../../../_TS_TABLE";
import {COLORS} from "@res/colors";
import {padding_rl_med} from "@styles/styles";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {DB_Organization, ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {ICONS} from "@res/icons";
import {AddOrganizationDialog} from "../organizations/AddOrganizationDialog";
import {DeleteOrganizationDialog} from "../organizations/DeleteOrganizationDialog";
import {SearchBar} from "../SearchBar";
import {OKButton} from "../../widget/OKButton";
import {AppPermissions} from "../../../utils/Permissions";
import {AppRoleLevel} from "@app/app-shared/permission";
import {OrganizationsContext, OrganizationsContextType} from "../organizations/OrganizationsContext";

type OrganizationsTableRow = {
    _id: DB_Organization
    name: DB_Organization
    actions: DB_Organization
    type: ORGANIZATION_TYPE
    hideDisenrolledCustomers?: boolean
}

type State = {
    searchText: string
}

export class OrganizationsAdminTab
    extends BaseComponent<{}, State> {

    constructor(props: {}) {
        super(props);
        this.state = {
            searchText: ""
        };
    }

    private getOrganizationsContent = (organizations: DB_Organization[]): OrganizationsTableRow[] => (
        organizations.filter(o => o.name.toLowerCase().includes(this.state.searchText.toLowerCase())).map(_organization => ({
            _id: _organization,
            name: _organization,
            actions: _organization,
            type: _organization.type,
            hideDisenrolledCustomers: _organization.hideDisenrolledCustomers
        })));

    headerStyle = (title: string, style?: CSSProperties) => {
        return <div className={`ll_h_c ${padding_rl_med}`}
                    style={{...style, height: 34, backgroundColor: COLORS.color_gray_medium}}>
            {title && FONTS.reg_14_secondary_dark(title, {whiteSpace: "nowrap"})}
        </div>;
    };

    rowStyle = (content: string) => {
        const textStyle = {color: COLORS.neutral_text_dark, lineHeight: "24px"}; // line height should be at least half of row height for maximum of 2 lines
        const blockWithText = emotion.css({maxHeight: "47px"});

        return <div
            className={`ll_h_c ${padding_rl_med}`}
            style={{height: 47, cursor: "pointer"}}>
            <div className={blockWithText}>
                {FONTS.reg_16(content, textStyle)}
            </div>
        </div>;
    };

    private header: (keyof OrganizationsTableRow)[] = ["name", "_id", "type", "hideDisenrolledCustomers", "actions"];

    renderOrganizationsTable = (orgCtx: OrganizationsContextType) => {
        const organizations = Object.values(orgCtx.organizations);
        return <div style={{...tableCardStyle, minHeight: "80%", minWidth: "80%"}}>
            <TS_Table<OrganizationsTableRow>
                id={"admin-table"}
                header={this.header}
                rows={this.getOrganizationsContent(organizations)}
                td={{
                    style: {borderBottom: `1px solid ${COLORS.grays_2}`}
                }}
                style={{width: "100%"}}
                headerRenderer={{
                    _id: () => this.headerStyle("Id"),
                    name: () => this.headerStyle("Name"),
                    type: () => this.headerStyle("Type"),
                    hideDisenrolledCustomers: () => this.headerStyle("Hide Disenrolled Customers"),
                    actions: () => this.headerStyle("Actions", {justifyContent: "center"})
                }}
                cellRenderer={{
                    _id: (cellValue: DB_Organization) => <div>
                        <div style={{
                            alignSelf: "flex-start",
                            height: "35px"
                        }} className="ll_h_c">
                            {this.rowStyle(cellValue._id)}</div>
                    </div>,
                    name: (cellValue: DB_Organization) => <div>
                        <div style={{
                            alignSelf: "flex-start",
                            height: "35px"
                        }} className="ll_h_c">
                            {this.rowStyle(cellValue.name)}</div>
                    </div>,
                    type: (cellValue: ORGANIZATION_TYPE) => <div>
                        <div style={{
                            alignSelf: "flex-start",
                            height: "35px"
                        }} className="ll_h_c">
                            {this.rowStyle(cellValue)}</div>
                    </div>,
                    hideDisenrolledCustomers: (cellValue?: boolean) => <div>
                        <div style={{
                            alignSelf: "flex-start",
                            height: "35px"
                        }} className="ll_h_c">
                            {this.rowStyle(cellValue ? cellValue.toString() : 'false')}</div>
                    </div>,
                    actions: (cellValue: DB_Organization) => <div className={"ll_h_c"}
                                                                  style={{justifyContent: "space-around"}}>
                        <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                            <div key={"edit"}
                                 className={"ll_h_c"}
                                 onClick={() => {
                                     AddOrganizationDialog.show(cellValue);
                                 }}
                                 style={{cursor: "pointer"}}>
                                {ICONS.icon_filters(COLORS.grays_dark, 20)}
                            </div>
                        </AppPermissions>
                        <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                            <div key={"delete"}
                                 className={"ll_h_c"}
                                 onClick={() => {
                                     DeleteOrganizationDialog.show(cellValue);
                                 }}
                                 style={{cursor: "pointer"}}>
                                {ICONS.x(COLORS.red, 20)}
                            </div>
                        </AppPermissions>
                    </div>
                }}
            />
        </div>;
    };


    render() {
        return <OrganizationsContext.Consumer>
            {orgCtx => <div style={{overflow: "scroll", marginTop: "15px"}} className={"match_height"}>
                <div style={{display: "flex", alignItems: "center"}}>
                    <SearchBar value={this.state.searchText} onValueChange={(value) => this.setState({searchText: value})}
                               onClose={() => this.setState({searchText: ""})}/>
                    <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                        <OKButton
                            text={"Add Organization"}
                            enabled={true}
                            onClick={() => AddOrganizationDialog.show()}
                            wrapperStyle={{
                                minHeight: "unset",
                                minWidth: "unset"
                            }}
                            inverted
                        />
                    </AppPermissions>
                </div>
                {this.renderOrganizationsTable(orgCtx)}
            </div>}
        </OrganizationsContext.Consumer>
    }
}
